/**
 * Error Handler
 * 
 * Centralized error handling for API responses.
 * Provides user-friendly error messages and consistent error handling across the application.
 */

import { toast } from 'react-toastify';
import { HTTP_STATUS } from '../config/api.config';

/**
 * Parse API error response and extract meaningful error message
 * @param {Error} error - Axios error object
 * @returns {Object} Parsed error object with user-friendly message
 */
export const parseApiError = (error) => {
  // Default error object
  const parsedError = {
    message: 'An unexpected error occurred. Please try again.',
    code: 'UNKNOWN_ERROR',
    status: 500,
    details: null,
  };

  // No response from server (network error)
  if (!error.response) {
    parsedError.message = 'Unable to connect to server. Please check your internet connection.';
    parsedError.code = 'NETWORK_ERROR';
    parsedError.status = 0;
    return parsedError;
  }

  // Extract status code
  parsedError.status = error.response.status;

  // Extract error message from response
  const responseData = error.response.data;
  
  if (responseData) {
    // Handle different error response formats
    if (responseData.error) {
      parsedError.message = responseData.error;
      parsedError.code = responseData.code || 'API_ERROR';
    } else if (responseData.message) {
      parsedError.message = responseData.message;
      parsedError.code = responseData.code || 'API_ERROR';
    } else if (typeof responseData === 'string') {
      parsedError.message = responseData;
    }
    
    // Store additional details if available
    if (responseData.details) {
      parsedError.details = responseData.details;
    }
  }

  // Specific handling for common HTTP status codes
  switch (parsedError.status) {
    case HTTP_STATUS.BAD_REQUEST:
      if (!responseData || !responseData.error) {
        parsedError.message = 'Invalid request. Please check your input.';
      }
      break;
      
    case HTTP_STATUS.UNAUTHORIZED:
      parsedError.message = 'Your session has expired. Please login again.';
      parsedError.code = 'UNAUTHORIZED';
      break;
      
    case HTTP_STATUS.FORBIDDEN:
      parsedError.message = 'You do not have permission to perform this action.';
      parsedError.code = 'FORBIDDEN';
      break;
      
    case HTTP_STATUS.NOT_FOUND:
      if (!responseData || !responseData.error) {
        parsedError.message = 'The requested resource was not found.';
      }
      break;
      
    case HTTP_STATUS.CONFLICT:
      if (!responseData || !responseData.error) {
        parsedError.message = 'A conflict occurred. The resource may already exist.';
      }
      break;
      
    case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      parsedError.message = 'A server error occurred. Please try again later.';
      break;
      
    case HTTP_STATUS.SERVICE_UNAVAILABLE:
      parsedError.message = 'Service temporarily unavailable. Please try again later.';
      break;
      
    default:
      // Keep the extracted message or use default
      break;
  }

  return parsedError;
};

/**
 * Display error toast notification
 * @param {Error|Object} error - Error object or parsed error
 * @param {Object} options - Toast options
 */
export const showErrorToast = (error, options = {}) => {
  const parsedError = error.message ? error : parseApiError(error);
  
  toast.error(parsedError.message, {
    position: toast.POSITION.BOTTOM_CENTER,
    autoClose: 5000,
    ...options,
  });
};

/**
 * Display success toast notification
 * @param {string} message - Success message
 * @param {Object} options - Toast options
 */
export const showSuccessToast = (message, options = {}) => {
  toast.success(message, {
    position: toast.POSITION.BOTTOM_CENTER,
    autoClose: 3000,
    ...options,
  });
};

/**
 * Display info toast notification
 * @param {string} message - Info message
 * @param {Object} options - Toast options
 */
export const showInfoToast = (message, options = {}) => {
  toast.info(message, {
    position: toast.POSITION.BOTTOM_CENTER,
    autoClose: 3000,
    ...options,
  });
};

/**
 * Display warning toast notification
 * @param {string} message - Warning message
 * @param {Object} options - Toast options
 */
export const showWarningToast = (message, options = {}) => {
  toast.warning(message, {
    position: toast.POSITION.BOTTOM_CENTER,
    autoClose: 4000,
    ...options,
  });
};

/**
 * Handle authentication errors
 * Redirects to login page and clears tokens
 * @param {Function} clearTokensCallback - Function to clear tokens
 * @param {Function} redirectCallback - Function to redirect to login
 */
export const handleAuthError = (clearTokensCallback, redirectCallback) => {
  if (clearTokensCallback) {
    clearTokensCallback();
  }
  
  showErrorToast({ message: 'Your session has expired. Please login again.' });
  
  if (redirectCallback) {
    setTimeout(() => {
      redirectCallback();
    }, 1000);
  }
};

/**
 * Check if error is an authentication error
 * @param {Error} error - Error object
 * @returns {boolean} True if authentication error
 */
export const isAuthError = (error) => {
  return error.response && error.response.status === HTTP_STATUS.UNAUTHORIZED;
};

/**
 * Check if error is a network error
 * @param {Error} error - Error object
 * @returns {boolean} True if network error
 */
export const isNetworkError = (error) => {
  return !error.response || error.code === 'ECONNABORTED' || error.message === 'Network Error';
};

/**
 * Check if error is a validation error
 * @param {Error} error - Error object
 * @returns {boolean} True if validation error
 */
export const isValidationError = (error) => {
  return error.response && error.response.status === HTTP_STATUS.BAD_REQUEST;
};

/**
 * Log error to console in development mode
 * @param {Error} error - Error object
 * @param {string} context - Context where error occurred
 */
export const logError = (error, context = 'Unknown') => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}] Error:`, error);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
    }
  }
};

/**
 * Handle error with logging and toast notification
 * @param {Error} error - Error object
 * @param {string} context - Context where error occurred
 * @param {boolean} showToast - Whether to show toast notification
 * @returns {Object} Parsed error object
 */
export const handleError = (error, context = 'API', showToast = true) => {
  logError(error, context);
  
  const parsedError = parseApiError(error);
  
  if (showToast) {
    showErrorToast(parsedError);
  }
  
  return parsedError;
};

export default {
  parseApiError,
  showErrorToast,
  showSuccessToast,
  showInfoToast,
  showWarningToast,
  handleAuthError,
  isAuthError,
  isNetworkError,
  isValidationError,
  logError,
  handleError,
};
